import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography
} from '@material-tailwind/react';
import axios from 'axios';
import { rootUrl } from '../../../config/config';
import SearchableSelect from '../../common/SearchableSelect';

/**
 * Dialog to move a job card to a different owner (requires job_card.move permission)
 */
export default function MoveJobCardDialog({ open, onClose, jobCardId, jobCardNo, currentOwnerCode, currentOwnerName, onSuccess }) {
    const token = localStorage.getItem('authToken') || '';
    const [ownerOptions, setOwnerOptions] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedOwner(null);
            setError('');
            const fetchOwners = async () => {
                try {
                    const res = await axios.get(`${rootUrl}/owners`, { headers: { Authorization: `Bearer ${token}` } });
                    const arr = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                    setOwnerOptions(arr.map(o => ({ value: o.code, label: `${o.code} - ${o.name}` })));
                } catch (err) {
                    console.error('Error loading owners:', err);
                }
            };
            fetchOwners();
        }
    }, [open, token]);

    const handleMove = async () => {
        if (!selectedOwner) {
            setError('Please select an owner');
            return;
        }
        if (selectedOwner === currentOwnerCode) {
            setError('Cannot move to the same owner');
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            const res = await axios.post(
                `${rootUrl}/api/job-cards/${jobCardId}/move`,
                { new_owner_code: selectedOwner },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data?.success) {
                onSuccess?.();
                onClose();
            } else {
                setError(res.data?.message || 'Move failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Move failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} handler={onClose} size="sm">
            <DialogHeader>Move Job Card to Different Owner</DialogHeader>
            <DialogBody>
                <Typography variant="paragraph" className="mb-3">
                    Move job card <strong>{jobCardNo}</strong> to a different owner.
                    {currentOwnerCode && ` Current owner: ${currentOwnerName ? `${currentOwnerName} (${currentOwnerCode})` : currentOwnerCode}`}
                </Typography>
                <Typography variant="small" color="gray" className="mb-3">
                    If invoiced, the original invoice will be reversed and a new one created for the new owner.
                </Typography>
                <div className="mb-3">
                    <Typography variant="small" className="mb-1">New owner</Typography>
                    <SearchableSelect
                        options={ownerOptions}
                        value={selectedOwner || null}
                        onChange={(val) => { setSelectedOwner(val || ''); setError(''); }}
                        placeholder="Select new owner"
                    />
                </div>
                {error && <Typography variant="small" color="red">{error}</Typography>}
            </DialogBody>
            <DialogFooter>
                <Button variant="outlined" onClick={onClose} className="mr-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none border-slate-300">Cancel</Button>
                <Button onClick={handleMove} disabled={submitting || !selectedOwner} className="bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm shadow-none">
                    {submitting ? 'Moving...' : 'Move'}
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
